export { calculateObjectDifferences, Difference } from '../util/diff-util.js'

import { BinaryOrBase64 } from '../app-types.js'

/**
 * Convert input to Base64 string
 */
export function toBase64(binaryData: BinaryOrBase64): string {
	if (typeof binaryData === 'string') {
		// Assume already base64
		return binaryData
	}

	if (binaryData instanceof Uint8Array) {
		binaryData = toBuffer(binaryData)
	}

	if (binaryData instanceof ArrayBuffer) {
		const bytes = new Uint8Array(binaryData)
		return bytesToBase64(bytes)
	}

	if (typeof Buffer !== 'undefined' && binaryData instanceof Buffer) {
		return binaryData.toString('base64')
	}

	throw new Error('Unsupported input type')
}

/**
 * Convert input to ArrayBuffer
 */
export function toArrayBuffer(binaryData: BinaryOrBase64): ArrayBuffer {
	if (binaryData instanceof ArrayBuffer) {
		return binaryData
	}

	if (binaryData instanceof Uint8Array) {
		binaryData = toBuffer(binaryData)
	}

	if (typeof Buffer !== 'undefined' && binaryData instanceof Buffer) {
		return binaryData.buffer.slice(
			binaryData.byteOffset,
			binaryData.byteOffset + binaryData.byteLength,
		) as ArrayBuffer
	}

	if (typeof binaryData === 'string') {
		const bytes = base64ToBytes(binaryData)
		return bytes.buffer as ArrayBuffer
	}

	throw new Error('Unsupported input type')
}

/**
 * Convert input to Node.js Buffer
 */
export function toBuffer(binaryData: BinaryOrBase64): Buffer {
	if (typeof Buffer === 'undefined') {
		throw new Error('Buffer is not available in this environment')
	}

	if (binaryData instanceof Buffer) {
		return binaryData
	}

	if (binaryData instanceof ArrayBuffer) {
		return Buffer.from(binaryData)
	}

	if (typeof binaryData === 'string') {
		return Buffer.from(binaryData, 'base64')
	}

	if (binaryData instanceof Uint8Array) {
		return Buffer.from(binaryData)
	}

	throw new Error('Unsupported input type')
}

/**
 * Helper: Convert Uint8Array → Base64
 */
function bytesToBase64(bytes: Uint8Array): string {
	if (typeof Buffer !== 'undefined') {
		return Buffer.from(bytes).toString('base64')
	}

	// Browser fallback
	let binary = ''
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i])
	}
	return btoa(binary)
}

/**
 * Helper: Convert Base64 → Uint8Array
 */
function base64ToBytes(base64: string): Uint8Array {
	if (typeof Buffer !== 'undefined') {
		return new Uint8Array(Buffer.from(base64, 'base64'))
	}

	// Browser fallback
	const binary = atob(base64)
	const bytes = new Uint8Array(binary.length)
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i)
	}
	return bytes
}
